import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { Plus, ExternalLink, Copy, X, Radio } from "lucide-react";

const STATUS_COLORS = {
  operational: "text-[#00F5D4] bg-[#00F5D4]/10 border-[#00F5D4]/20",
  degraded: "text-amber-400 bg-amber-500/10 border-amber-500/20",
  incident: "text-rose-400 bg-rose-500/10 border-rose-500/20",
  maintenance: "text-blue-400 bg-blue-500/10 border-blue-500/20",
};

const STATUSES = ["operational", "degraded", "incident", "maintenance"];

export default function StatusPages() {
  const [pages, setPages] = useState([]);
  const [projects, setProjects] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [activeUpdate, setActiveUpdate] = useState(null); // page to post update on
  const [form, setForm] = useState({ project_id: "", slug: "", title: "", description: "", is_public: true, show_tasks: true, show_milestones: true, show_health: true });
  const [updateForm, setUpdateForm] = useState({ status: "operational", title: "", body: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const [pRes, projRes] = await Promise.all([
        api.get("/status-pages"),
        api.get("/projects").catch(() => ({ data: [] })),
      ]);
      setPages(pRes.data || []);
      setProjects(projRes.data || []);
    } catch {
      toast.error("Failed to load status pages");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createPage = async () => {
    if (!form.slug || !form.title || !form.project_id) { toast.error("Project, slug and title required"); return; }
    setSaving(true);
    try {
      await api.post("/status-pages", form);
      toast.success("Status page created");
      setShowCreate(false);
      setForm({ project_id: "", slug: "", title: "", description: "", is_public: true, show_tasks: true, show_milestones: true, show_health: true });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create page");
    } finally {
      setSaving(false);
    }
  };

  const deletePage = async (page) => {
    if (!window.confirm(`Delete status page "${page.title}"?`)) return;
    try {
      await api.delete(`/status-pages/${page.page_id}`);
      toast.success("Deleted");
      load();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const postUpdate = async () => {
    if (!updateForm.title) { toast.error("Title required"); return; }
    setSaving(true);
    try {
      await api.post(`/status-pages/${activeUpdate.page_id}/updates`, updateForm);
      toast.success("Update posted");
      setActiveUpdate(null);
      setUpdateForm({ status: "operational", title: "", body: "" });
    } catch {
      toast.error("Failed to post update");
    } finally {
      setSaving(false);
    }
  };

  const copyLink = (page) => {
    const url = `${window.location.origin}/status/${page.slug}`;
    navigator.clipboard.writeText(url).then(() => toast.success("Link copied"));
  };

  const projName = (id) => projects.find(p => p.project_id === id)?.name || id;

  return (
    <DarkPage><div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Status Pages</h1>
          <p className="text-sm text-[#7d8590] mt-1">Public embeddable status pages for external stakeholders</p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
          <Plus className="w-4 h-4 mr-1" /> New Status Page
        </Button>
      </div>

      {pages.length === 0 ? (
        <div className="text-center py-20">
          <Radio className="w-10 h-10 text-[#484f58] mx-auto mb-3" />
          <p className="text-[#7d8590] text-sm">No status pages yet. Create one to share project health with clients.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {pages.map(page => (
            <div key={page.page_id} className="bg-[#0d1117] border border-[#21262d] rounded-lg p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="text-white font-semibold">{page.title}</h3>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${page.is_public ? "text-[#00F5D4] bg-[#00F5D4]/10 border-[#00F5D4]/20" : "text-[#7d8590] bg-[#1c2128] border-[#30363d]"}`}>
                      {page.is_public ? "Public" : "Private"}
                    </span>
                  </div>
                  {page.description && <p className="text-sm text-[#7d8590] mb-2">{page.description}</p>}
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xs text-[#484f58]">📁 {projName(page.project_id)}</span>
                    <code className="text-xs text-[#7d8590] bg-[#161b22] border border-[#30363d] rounded px-2 py-0.5">/status/{page.slug}</code>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => copyLink(page)} title="Copy public link"
                    className="p-2 text-[#7d8590] hover:text-white bg-[#161b22] border border-[#30363d] rounded-lg">
                    <Copy className="w-4 h-4" />
                  </button>
                  <a href={`/status/${page.slug}`} target="_blank" rel="noopener noreferrer"
                    className="p-2 text-[#7d8590] hover:text-white bg-[#161b22] border border-[#30363d] rounded-lg">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                  <button onClick={() => { setActiveUpdate(page); setUpdateForm({ status: "operational", title: "", body: "" }); }}
                    className="px-3 py-2 text-xs bg-[#161b22] border border-[#30363d] text-[#7d8590] hover:text-white rounded-lg">
                    Post update
                  </button>
                  <button onClick={() => deletePage(page)} className="p-2 text-[#484f58] hover:text-rose-400 bg-[#161b22] border border-[#30363d] rounded-lg">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create modal */}
      {showCreate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">New Status Page</h2>
              <button onClick={() => setShowCreate(false)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Project *</label>
                <select value={form.project_id} onChange={e => setForm(f => ({ ...f, project_id: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
                  <option value="">Select project…</option>
                  {projects.map(p => <option key={p.project_id} value={p.project_id}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Title *</label>
                <input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Acme Corp Project Status"
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">
                  URL slug * <span className="text-[#484f58] font-normal">(lowercase, hyphens only)</span>
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#484f58]">/status/</span>
                  <input value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "") }))}
                    placeholder="acme-project"
                    className="flex-1 bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Description (optional)</label>
                <input value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div className="flex flex-wrap gap-4">
                {[["is_public", "Public"], ["show_health", "Show health"], ["show_tasks", "Show tasks"], ["show_milestones", "Show milestones"]].map(([k, lbl]) => (
                  <label key={k} className="flex items-center gap-2 text-sm text-[#7d8590] cursor-pointer">
                    <input type="checkbox" checked={form[k]} onChange={e => setForm(f => ({ ...f, [k]: e.target.checked }))} className="accent-[#00F5D4]" />
                    {lbl}
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={createPage} disabled={saving} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">Create</Button>
              <Button onClick={() => setShowCreate(false)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {/* Post update modal */}
      {activeUpdate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-[#0d1117] border border-[#21262d] rounded-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-white font-semibold">Post Update — {activeUpdate.title}</h2>
              <button onClick={() => setActiveUpdate(null)} className="text-[#7d8590] hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-xs text-[#7d8590] mb-2 block">Status</label>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map(s => (
                    <button key={s} onClick={() => setUpdateForm(f => ({ ...f, status: s }))}
                      className={`px-3 py-1.5 text-xs rounded-full border capitalize transition-colors ${updateForm.status === s ? STATUS_COLORS[s] : "border-[#30363d] text-[#7d8590]"}`}>
                      {s}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Title *</label>
                <input value={updateForm.title} onChange={e => setUpdateForm(f => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. All systems operational"
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="text-xs text-[#7d8590] mb-1 block">Body (optional)</label>
                <textarea value={updateForm.body} onChange={e => setUpdateForm(f => ({ ...f, body: e.target.value }))} rows={3}
                  className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm resize-none" />
              </div>
            </div>
            <div className="flex gap-2 mt-5">
              <Button onClick={postUpdate} disabled={saving} className="flex-1 bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">Post update</Button>
              <Button onClick={() => setActiveUpdate(null)} variant="outline" className="border-[#30363d] text-[#7d8590]">Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div></DarkPage>
  );
}
